import React, { useState, useEffect } from 'react';
import InputField from '../Forms/Input/InputField';
import { useCashSession } from '../../hooks/cash/useCashSession';
import PanelCard from '../ui/PanelCard';

const bills = [100, 50, 20, 10, 5, 1];
const coins = [0.25, 0.1, 0.05, 0.01];

const denominations = [...bills, ...coins];

const CashOpeningDenominationSheet: React.FC = () => {
  const { updateOpeningDetails } = useCashSession();

  const [quantities, setQuantities] = useState<Record<number, number>>({});

  const handleChange = (denomination: number, value: number) => {
    setQuantities((prev) => ({
      ...prev,
      [denomination]: value,
    }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };


  const getSubtotal = (d: number) => {
    const qty = quantities[d] || 0;
    return d * qty;
  };

  /**
   * Calcular total y enviar a redux
   */
  useEffect(() => {
    const details: any[] = [];
    let total = 0;

    denominations.forEach((d) => {
      const qty = quantities[d] || 0;
      const subtotal = d * qty;

      if (qty > 0) {
        details.push({
          denomination: d.toFixed(2),
          quantity: qty,
          subtotal: subtotal.toFixed(2),
        });
      }

      total += subtotal;
    });

    updateOpeningDetails(details, total);
  }, [quantities]);

  /**
   * Bloquear teclas no permitidas
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(e.key)) return;

    // Bloquear Enter y Escape
    if (e.key === 'Enter' || e.key === 'Escape') {
      e.preventDefault();
      return;
    }

    // Permitir solo dígitos
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const renderRow = (d: number, type: 'bill' | 'coin') => {
    const subtotal = getSubtotal(d);

    const iconKey = type === 'bill' ? 'bill' : 'coin';

    return (
      <div key={d} className="flex items-end gap-3">
        <div className="flex-1">
          <InputField
            label={`$${d.toFixed(2)}`}
            name={`denomination-${d}`}
            type="number"
            value={quantities[d] || ''}
            min={0}
            step="1"
            iconKey={iconKey}
            rightContent={`= $${formatCurrency(subtotal)}`}
            onKeyDown={handleKeyDown}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => {
              const value = e.target.value;

              if (value === '') {
                handleChange(d, 0);
                return;
              }

              const clean = value.replace(/[^0-9]/g, '');
              const parsed = parseInt(clean, 10);

              if (!isNaN(parsed)) {
                handleChange(d, parsed);
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <PanelCard title="Hoja de Conteo">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <h4 className="mb-3 font-semibold text-black dark:text-white">
            Billetes
          </h4>

          <div className="space-y-3">
            {bills.map((d) => renderRow(d, 'bill'))}
          </div>
        </div>

        <div>
          <h4 className="mb-3 font-semibold text-black dark:text-white">
            Monedas
          </h4>

          <div className="space-y-3">
            {coins.map((d) => renderRow(d, 'coin'))}
          </div>
        </div>
      </div>
    </PanelCard>
  );
};

export default CashOpeningDenominationSheet;
